import axios from 'axios';
import * as config from 'config';
import debug from 'debug';
import session from 'lib/session';
import { isArray, isFunction } from 'lodash';
import { stringify } from 'qs';
import { create } from './actions';

const DEBUG = debug('api');

const createUrl = (strings, keys) =>
  strings
    .map((s, i) =>
      keys[i] ? s + (isFunction(keys[i]) ? keys[i]() : keys[i]) : s
    )
    .join('');

const replaceParams = function (pattern, params) {
  return pattern.replace(/:[a-z|A-Z]+/g, (match) => {
    const matchedParam = match.substr(1);
    const value = params[matchedParam];
    if (typeof value === 'undefined') {
      throw new Error(
        `Matched param "${matchedParam}" is not presented at given object`
      );
    }
    return value;
  });
};

export const adminApiInstance = axios.create({
  baseURL: `${config.adminApi.url}/${config.adminApi.version}`,
});

export const adminApiLegacyInstance = axios.create({
  baseURL: `${config.adminApiLegacy.url}/${config.adminApiLegacy.version}`,
});

export const analyticsApiInstance = axios.create({
  baseURL: `${config.analyticsApi.url}/${config.analyticsApi.version}`,
});

export const searchApiInstance = axios.create({
  baseURL: `${config.searchApi.url}/${config.searchApi.version}`,
});

export const createApiCall =
  (
    method: string,
    strings: string[],
    keys: any[],
    useLegacyAdminApi: boolean
  ) =>
  (data, params) => {
    const normalizedUrl: string = replaceParams(createUrl(strings, keys), {
      ...data,
      ...params,
    });
    const adminApiInstanceTargetted = useLegacyAdminApi
      ? adminApiLegacyInstance
      : adminApiInstance;
    return adminApiInstanceTargetted.request({
      url:
        session.merchant && !normalizedUrl.startsWith('/')
          ? `merchants/${session.getMerchant()}/${normalizedUrl}`
          : normalizedUrl,
      headers: {
        ...(!!session.token ? { [config.adminApi.token]: session.token } : {}),
        ...(!!session.kcToken ? { kc: session.kcToken } : {}),
        timestamp: new Date().getTime(),
      },
      method,
      data: data || {},
      params,
    });
  };

const analyticsApiCall = (props) => (data) =>
  analyticsApiInstance.request({
    method: 'post',
    timeout: 20000,
    headers: {
      [config.adminApi.token]: session.token,
      [config.adminApi.kcToken]: session.kcToken,
    },
    data: {
      merchant: session.key,
      limit: -1,
      ...props,
      ...data,
    },
  });

export const searchApiCall =
  (method: string, url: string[] | string, keys?: any[]) => (data, params) => {
    const normalizedUrl: string = isArray(url)
      ? replaceParams(createUrl(url, keys), { ...data, ...params })
      : url;
    const _data = {
      ...((data && data) || {}),
      log: false,
      user: { uid: '1', sid: '1' },
      t_client: new Date().getTime(),
      key: session.key,
    };
    return searchApiInstance.request({
      url: normalizedUrl,
      method,
      data: method === 'post' ? _data : undefined,
      params: method === 'get' ? _data : undefined,
      paramsSerializer: stringify,
      headers: {
        'x-key': session.key,
      },
    });
  };

const createRequest =
  (method) =>
  (strings, ...keys) =>
  (responseMapper?, useLegacyAdminApi?) => {
    const path = strings.join('');
    return create(
      `[${method}]${path}`,
      createApiCall(method, strings, keys, useLegacyAdminApi),
      responseMapper
    );
  };

const createAnalyticsRequest = (props) => (responseMapper?) => {
  const label = Object.keys(props)
    .map((key) => JSON.stringify(props[key]))
    .join('');
  return create(
    `[analytics]${label}`,
    analyticsApiCall(props),
    responseMapper(props)
  );
};

const createSearchRequest =
  (method) =>
  (strings, ...keys) =>
  (responseMapper?) => {
    const path = strings.join('');
    return create(
      `[${method}]${path}`,
      searchApiCall(method, strings, keys),
      responseMapper
    );
  };

export const get = createRequest('get');
export const put = createRequest('put');
export const post = createRequest('post');
export const del = createRequest('delete');
export const analytics = createAnalyticsRequest;
export const search = {
  get: createSearchRequest('get'),
  post: createSearchRequest('post'),
};
